import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  ArrowLeftIcon, 
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import ContactButton from '../components/ContactButton';
import LoadingSpinner from '../components/LoadingSpinner';

const FeaturedInventoryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const { data: vehicle, isLoading, error } = useQuery(
    ['featured-inventory', id],
    async () => {
      const response = await fetch(`/api/v1/featured-inventory/${id}`);
      if (!response.ok) throw new Error('Failed to fetch vehicle');
      return response.json();
    }
  );

  const nextImage = () => {
    if (vehicle?.images) {
      setCurrentImageIndex((prev) => (prev + 1) % vehicle.images.length);
    }
  };

  const prevImage = () => {
    if (vehicle?.images) {
      setCurrentImageIndex((prev) => (prev - 1 + vehicle.images.length) % vehicle.images.length);
    }
  };

  // Keyboard navigation for image modal
  React.useEffect(() => {
    if (!isImageModalOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const imagesLength = vehicle?.images?.length || 0;
      switch (e.key) {
        case 'Escape':
          setIsImageModalOpen(false);
          break;
        case 'ArrowLeft':
          if (imagesLength > 1) prevImage();
          break;
        case 'ArrowRight':
          if (imagesLength > 1) nextImage();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageModalOpen, vehicle?.images?.length]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Vehicle Not Found</h2>
          <Link to="/" className="text-primary-600 hover:text-primary-700">
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const images = vehicle.images || [];
  const currentImage = images[currentImageIndex];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back to Homepage
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="relative aspect-[4/3] bg-gray-200">
                {currentImage ? (
                  <img
                    src={currentImage.image_url}
                    alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                    className="w-full h-full object-cover cursor-pointer hover:opacity-95 transition-opacity"
                    onClick={() => setIsImageModalOpen(true)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <p className="text-gray-400">No Image Available</p>
                  </div>
                )}
                
                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    >
                      <ChevronLeftIcon className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    >
                      <ChevronRightIcon className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                {images.length > 1 && (
                  <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                )}
              </div>
            </div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="grid grid-cols-6 gap-2">
                {images.map((image: any, index: number) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      index === currentImageIndex
                        ? 'border-primary-600 scale-105'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img
                      src={image.image_url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Details */}
          <div className="space-y-6">
            {/* Title and Price */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h1>
                  {vehicle.title && (
                    <p className="text-lg text-gray-600">{vehicle.title}</p>
                  )}
                </div>
                {vehicle.is_available && !vehicle.is_sold && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                    Available
                  </span>
                )}
              </div>

              <div className="border-t border-b border-gray-200 py-4 my-4">
                <p className="text-sm text-gray-600 mb-1">Цена</p>
                <p className="text-4xl font-bold text-green-600">
                  {Math.round(vehicle.price).toLocaleString()} €
                </p>
                <p className="text-lg text-gray-500">
                  ({Math.round(vehicle.price * 1.96).toLocaleString()} лв)
                </p>
              </div>

              <ContactButton 
                vehicleId={vehicle.id?.toString()} 
                vehicleName={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                variant="primary"
                className="w-full"
              />
            </div>

            {/* Specifications */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Specifications</h2>
              <div className="grid grid-cols-2 gap-4">
                {vehicle.engine && (
                  <div>
                    <p className="text-sm text-gray-600">Engine</p>
                    <p className="font-semibold text-gray-900">{vehicle.engine}</p>
                  </div>
                )}
                {vehicle.transmission && (
                  <div>
                    <p className="text-sm text-gray-600">Transmission</p>
                    <p className="font-semibold text-gray-900">{vehicle.transmission}</p>
                  </div>
                )}
                {vehicle.drive_type && (
                  <div>
                    <p className="text-sm text-gray-600">Drive Type</p>
                    <p className="font-semibold text-gray-900">{vehicle.drive_type}</p>
                  </div>
                )}
                {vehicle.fuel_type && (
                  <div>
                    <p className="text-sm text-gray-600">Fuel Type</p>
                    <p className="font-semibold text-gray-900">{vehicle.fuel_type}</p>
                  </div>
                )}
                {vehicle.body_style && (
                  <div>
                    <p className="text-sm text-gray-600">Body Style</p>
                    <p className="font-semibold text-gray-900">{vehicle.body_style}</p>
                  </div>
                )}
                {vehicle.exterior_color && (
                  <div>
                    <p className="text-sm text-gray-600">Exterior Color</p>
                    <p className="font-semibold text-gray-900">{vehicle.exterior_color}</p>
                  </div>
                )}
                {vehicle.interior_color && (
                  <div>
                    <p className="text-sm text-gray-600">Interior Color</p>
                    <p className="font-semibold text-gray-900">{vehicle.interior_color}</p>
                  </div>
                )}
                {vehicle.odometer && (
                  <div>
                    <p className="text-sm text-gray-600">Odometer</p>
                    <p className="font-semibold text-gray-900">
                      {vehicle.odometer.toLocaleString()} {vehicle.odometer_unit}
                    </p>
                  </div>
                )}
                {vehicle.location && (
                  <div>
                    <p className="text-sm text-gray-600">Location</p>
                    <p className="font-semibold text-gray-900">{vehicle.location}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {vehicle.description && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Description</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{vehicle.description}</p>
              </div>
            )}

            {/* Features */}
            {vehicle.features && vehicle.features.length > 0 && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Features & Highlights</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {vehicle.features.map((feature: string, index: number) => (
                    <div key={index} className="flex items-start">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isImageModalOpen && currentImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-95 p-4"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div className="relative max-w-7xl max-h-full w-full">
            {/* Close button */}
            <button
              onClick={() => setIsImageModalOpen(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2"
              aria-label="Close"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Image navigation buttons */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-75 transition-all z-10"
                >
                  <ChevronLeftIcon className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-75 transition-all z-10"
                >
                  <ChevronRightIcon className="w-8 h-8" />
                </button>
              </>
            )}

            {/* Main image */}
            <img
              src={currentImage.image_url}
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} - Image ${currentImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain mx-auto rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturedInventoryDetailPage;

